import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Text from "@/components/ui/text";
import Banner from "@/components/ui/banner";
import Badge from "@/components/ui/badge";
import Tabs from "@/components/controls/Tabs";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import DreamerIcons from "@/components/controls/Questions/DreamerIndication/components/DreamerIcons";
import { StopTest, Refresh, TriangleArrowRight } from "@dreamhub/designsystem/icons";
import SingleSelect from "@/components/controls/SingleSelect";
import { useFilteredDeals } from "@/pages/deals/hooks";
import { DealData } from "@/pages/deals/types";
import { useFilteredContracts } from "@/pages/contracts/hooks";
import type { ContractData } from "@/pages/contracts/types";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { EmptyFilters } from "@/services/api/types";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import {
  AUTOFILL_TEST_PROGRESS_CEILING,
  AUTOFILL_TEST_PROGRESS_INTERVAL_MS,
  AUTOFILL_TEST_PROGRESS_STEP
} from "../const";
import {
  AutofillCandidate,
  AutofillTestRun,
  isTerminalTestRunStatus,
  useAutofillTestRunStatus,
  useCancelAutofillTest,
  useRunAutofillTest
} from "../hooks/useAutofillApi";
import {
  type AutofillTargetKind,
  useAutofillTarget
} from "../hooks/autofillTarget";
import PopoverHeader from "@/components/ui/popover-header";

interface TestAutofillPopoverProps {
  questionId: string;
  fieldName: string;
  fieldType: string;
  options: string[];
  instructions: string;
  triggeringActivityTypes: string[];
  translationPrefix: string;
  onClose: () => void;
}

const parseChips = (rawValue: string, renderedValue: string): string[] => {
  try {
    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      return parsed.map(String).filter(Boolean);
    }
  } catch {
    // rawValue isn't JSON — fall back to the comma-joined rendered string
  }
  return renderedValue
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
};

// The records a test can run against: deals for a deal question, contracts for a contract
// workflow item. Both lists are mounted so hook order is fixed; only one is enabled.
const useTestRecordOptions = (kind: AutofillTargetKind, search: string) => {
  const isContract = kind === "contractWorkflowItem";
  // Memoize the search object so its reference is stable across renders — an
  // inline `{ term }` churns the infinite-query deps and causes a render loop.
  const dealSearchQuery = useMemo(
    () => (search ? { term: search } : undefined),
    [search]
  );
  const { entities: dealEntities, isLoading: dealsLoading } = useFilteredDeals(
    EmptyFilters,
    dealSearchQuery,
    undefined,
    undefined,
    "name",
    true,
    false,
    !isContract
  );
  const { contracts, isLoading: contractsLoading } = useFilteredContracts(
    "name",
    true,
    EmptyFilters,
    search,
    isContract
  );
  const options = useMemo(
    () =>
      (
        (isContract ? contracts : dealEntities) as (DealData | ContractData)[]
      ).map((record) => ({ id: record.id, displayName: record.name })),
    [isContract, contracts, dealEntities]
  );
  return {
    options,
    isLoading: isContract ? contractsLoading : dealsLoading
  };
};

export const TestAutofillPopover: React.FC<TestAutofillPopoverProps> = ({
  questionId,
  fieldName,
  fieldType,
  options,
  instructions,
  triggeringActivityTypes,
  translationPrefix,
  onClose
}) => {
  const { t } = useTranslation();
  const testRun = useRunAutofillTest();
  const cancelTest = useCancelAutofillTest();
  const target = useAutofillTarget();

  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [selectedRecordName, setSelectedRecordName] = useState("");
  const [recordSearch, setRecordSearch] = useState("");
  const { options: recordOptions, isLoading: recordsLoading } =
    useTestRecordOptions(target.kind, recordSearch);

  const [testRunId, setTestRunId] = useState<string | null>(null);
  const [run, setRun] = useState<AutofillTestRun | null>(null);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<number | null>(null);

  const { data: polledRun } = useAutofillTestRunStatus(questionId, testRunId);

  useEffect(() => {
    if (polledRun) {
      setRun(polledRun);
    }
  }, [polledRun]);

  const status = run?.status;
  const isRunning = !!status && !isTerminalTestRunStatus(status);
  const isTerminal = !!status && isTerminalTestRunStatus(status);
  const candidate = status === "completed" ? run?.candidate : null;
  const failed = status === "error" || testRun.isError;
  const noResult = status === "completed" && !!candidate && !candidate.hasValue;

  const stopTimer = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Simulated progress while the run folds activities; completion is driven by
  // the polled terminal status, not the bar.
  useEffect(() => {
    if (!isRunning) {
      stopTimer();
      return;
    }
    intervalRef.current = window.setInterval(() => {
      setProgress((prev) =>
        Math.min(
          prev + AUTOFILL_TEST_PROGRESS_STEP,
          AUTOFILL_TEST_PROGRESS_CEILING
        )
      );
    }, AUTOFILL_TEST_PROGRESS_INTERVAL_MS);
    return stopTimer;
  }, [isRunning]);

  const resetRun = () => {
    setTestRunId(null);
    setRun(null);
    setProgress(0);
  };

  const handleSelectRecord = (recordId: string) => {
    const record = recordOptions.find(
      (option) => String(option.id) === recordId
    );
    setSelectedRecordId(recordId);
    setSelectedRecordName(record?.displayName ?? "");
    testRun.reset();
    resetRun();
  };

  const handleRun = async () => {
    if (!selectedRecordId) return;
    testRun.reset();
    resetRun();
    try {
      const created = await testRun.mutateAsync({
        pathParams: { questionId },
        data: {
          entityId: selectedRecordId,
          instructions,
          triggeringActivityTypes
        }
      });
      setRun(created);
      setTestRunId(created.id);
    } catch (error) {
      console.error("Failed to start autofill test run:", error);
    }
  };

  const handleStop = () => {
    if (testRunId) {
      cancelTest.mutate({ pathParams: { questionId, testRunId } });
    }
    stopTimer();
    testRun.reset();
    resetRun();
  };

  const renderActionButton = () => {
    if (isRunning) {
      return (
        <Button
          variant="secondary"
          className="h-9 gap-2 [&_svg]:size-4"
          onClick={handleStop}
        >
          <StopTest />
          {t(`${translationPrefix}.test.stop`)}
        </Button>
      );
    }
    return (
      <Button
        className="h-9 gap-2 [&_svg]:size-4"
        disabled={!selectedRecordId || testRun.isPending}
        onClick={handleRun}
      >
        {isTerminal ? <Refresh /> : <TriangleArrowRight />}
        {isTerminal
          ? t(`${translationPrefix}.test.runAgain`)
          : t(`${translationPrefix}.test.run`)}
      </Button>
    );
  };

  const renderResultValue = (candidate: AutofillCandidate) => {
    const { rawValue, renderedValue } = candidate;
    switch (fieldType) {
      case FieldDisplayType.MULTI_SELECT:
        return (
          <div className="flex flex-wrap gap-1 px-1">
            {parseChips(rawValue, renderedValue).map((chip) => (
              <Badge key={chip} text={chip} textVariant="body" />
            ))}
          </div>
        );
      case FieldDisplayType.SWITCH:
      case FieldDisplayType.BOOL: {
        const isBool = fieldType === FieldDisplayType.BOOL;
        return (
          <div className="w-fit px-1">
            <Tabs
              options={{
                option1: (!isBool && options[0]) || t("yes"),
                option2: (!isBool && options[1]) || t("no")
              }}
              value={rawValue === "option2" ? "option2" : "option1"}
              valueSelected={() => {}}
              isPositiveNegative
              size="lg"
            />
          </div>
        );
      }
      case FieldDisplayType.CHECKBOX:
      case FieldDisplayType.CHECKLIST_ITEM:
        return (
          <div className="pointer-events-none flex items-center px-1">
            <SingleCheckbox
              id={`autofill-test-checkbox-${questionId}`}
              label=""
              isChecked={rawValue === "checked" || rawValue === "true"}
              onCheckedChange={() => {}}
            />
          </div>
        );
      case FieldDisplayType.DATE: {
        const date = toLocalDate(rawValue || renderedValue);
        return (
          <Text variant="body" className="px-1">
            {Number.isNaN(date.getTime())
              ? renderedValue
              : simpleDate(date, "short", false, true)}
          </Text>
        );
      }
      default:
        return (
          <Text variant="body" className="px-1">
            {renderedValue}
          </Text>
        );
    }
  };

  const renderResultBody = () => {
    if (isRunning) {
      return (
        <div className="flex h-20 w-full items-center justify-center rounded-xl bg-gradient-to-r from-white/[0.04] to-white/[0.02]">
          <div className="flex w-[200px] flex-col items-center justify-center gap-2">
            <Text variant="smBodyTertiary" className="text-center">
              {t(`${translationPrefix}.test.running`, {
                record: selectedRecordName
              })}
            </Text>
            <Progress
              value={progress}
              indicatorClassname="!bg-white"
              levels={{ medium: -1, high: -1 }}
            />
          </div>
        </div>
      );
    }
    if (failed || noResult) {
      return (
        <div className="flex flex-col gap-1 rounded-xl border border-border-border1 bg-surface-elevation2 p-2">
          <Text variant="bodySecondary" className="pl-1">
            {fieldName}
          </Text>
          <Banner
            variant="warning"
            size="small"
            titleVariant="body"
            title={t(
              `${translationPrefix}.${
                failed ? "test.error" : target.copyKeys.noResult
              }`
            )}
            showCloseButton={false}
          />
        </div>
      );
    }
    if (candidate?.hasValue) {
      return (
        <div className="flex flex-col gap-1 rounded-xl border border-border-border1 bg-surface-elevation2 p-2">
          <div className="flex items-start justify-between gap-2 pl-1">
            <Text variant="bodySecondary">{fieldName}</Text>
            <div className="shrink-0">
              <DreamerIcons
                dreamerMode
                isLastEditedByDreamer
                lastEditedByData={undefined}
                previousLastEditedBy={null}
                size="sm"
              />
            </div>
          </div>
          {renderResultValue(candidate)}
        </div>
      );
    }
    return (
      <div className="flex h-20 w-full items-center justify-center rounded-xl border-[1.5px] border-dashed border-border-border2 bg-surface-elevation2 p-2">
        <Text variant="smBodyTertiary" className="text-center">
          {t(`${translationPrefix}.test.resultPlaceholder`)}
        </Text>
      </div>
    );
  };

  return (
    <PopoverContent
      align="end"
      side="top"
      sideOffset={8}
      padding={0}
      className="w-[450px]"
      onInteractOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("[data-radix-popper-content-wrapper]")) {
          e.preventDefault();
        }
      }}
    >
      <PopoverHeader
        title={t(`${translationPrefix}.test.title`)}
        onClose={onClose}
      />

      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-end gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Text variant="bodySecondary" className="pl-1">
              {t(`${translationPrefix}.${target.copyKeys.recordLabel}`)}
            </Text>
            <SingleSelect
              fieldName="test-autofill-record"
              variant="form"
              showSearch
              options={recordOptions}
              selectedValue={selectedRecordId}
              placeholder={t(
                `${translationPrefix}.${target.copyKeys.recordPlaceholder}`
              )}
              isLoading={recordsLoading}
              disabled={isRunning}
              onSearch={setRecordSearch}
              onSelection={(value) => handleSelectRecord(String(value))}
            />
          </div>
          {renderActionButton()}
        </div>

        <div className="flex flex-col gap-1">
          <Text variant="bodySecondary" className="pl-1">
            {t(`${translationPrefix}.test.resultLabel`)}
          </Text>
          {renderResultBody()}
        </div>
      </div>
    </PopoverContent>
  );
};
